import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { METODOS_DISTRIBUCION } from '../Services/nomina-directa.services';

/**
 * Participación de UNA persona: con qué método y entre qué centros se reparte su nómina.
 * Se usa igual en la ficha del administrativo (su reparto por defecto) y dentro de una
 * nómina en borrador (el ajuste de esa quincena), para que se vea y se edite igual en los
 * dos lados.
 */
const ParticipacionCentros = ({ metodo, centros, sedes, onChange, ayudaVacio }) => {
  const elegido = (id) => centros.find((c) => String(c.id) === String(id));

  const alternar = (sede) =>
    onChange({
      metodo,
      centros: elegido(sede.id)
        ? centros.filter((c) => String(c.id) !== String(sede.id))
        : [...centros, { id: sede.id, nombre: sede.nombre, porcentaje: '' }],
    });

  const setPorcentaje = (id, valor) =>
    onChange({
      metodo,
      centros: centros.map((c) => (String(c.id) === String(id) ? { ...c, porcentaje: valor } : c)),
    });

  const repartirIgual = () => {
    if (!centros.length) return;
    const parte = Math.round((100 / centros.length) * 100) / 100;
    const ajuste = Math.round((100 - parte * centros.length) * 100) / 100;
    onChange({
      metodo,
      centros: centros.map((c, i) => ({ ...c, porcentaje: i === 0 ? parte + ajuste : parte })),
    });
  };

  const suma = centros.reduce((s, c) => s + (Number(c.porcentaje) || 0), 0);

  return (
    <div className="space-y-2">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Forma de repartir</Label>
          <Select value={metodo} onValueChange={(v) => onChange({ metodo: v, centros })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(METODOS_DISTRIBUCION).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {metodo === 'porcentaje' && (
          <div className="space-y-2">
            <Label>Porcentajes</Label>
            <div className="flex items-center gap-2 text-sm">
              <span className={Math.abs(suma - 100) > 0.01 ? 'text-destructive' : 'text-muted-foreground'}>
                Suman {suma.toLocaleString('es-CO', { maximumFractionDigits: 2 })} % de 100 %
              </span>
              <button
                type="button"
                className="text-primary underline underline-offset-2"
                onClick={repartirIgual}
                disabled={!centros.length}
              >
                repartir igual
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="max-h-56 overflow-y-auto rounded-md border p-2 space-y-1">
        {sedes.length === 0 && <p className="text-sm text-muted-foreground">No se encontraron centros activos.</p>}
        {sedes.map((s) => {
          const c = elegido(s.id);
          return (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!c} onCheckedChange={() => alternar(s)} />
              <span className="flex-1 truncate">{s.nombre}</span>
              {metodo === 'porcentaje' && c && (
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="h-8 w-20 text-right"
                    value={c.porcentaje ?? ''}
                    onChange={(e) => setPorcentaje(s.id, e.target.value)}
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {centros.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {ayudaVacio || 'Sin centros elegidos: se usa el reparto por defecto de la nómina.'}
        </p>
      )}
      {metodo === 'kilos' && centros.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Se reparte según los kilos que reciba cada centro elegido en el periodo de la nómina.
        </p>
      )}
    </div>
  );
};

export default ParticipacionCentros;
